//@flow

import {useEffect} from 'react';
import {useSelector} from 'react-redux';

import {
  getProductVisibilityService,
  setProductVisibilityService,
} from './promises';
import {useActions} from '../../utils/globalStore';

import {actions} from './duck';

import type {ProductVisibility} from '../../entities';
import type {RootState} from '..';

const visibilitySettingsSelector = (state: RootState) => {
  const {enabledFeatured, enabledRecent, isLoading} = state.productVisibility;

  if (
    typeof enabledFeatured === 'undefined' &&
    typeof enabledRecent === 'undefined'
  )
    return {isLoading};

  return {
    isLoading,
    visibilitySettings: {
      enabledFeatured,
      enabledRecent,
    },
  };
};

export function useInitialProductVisibilitySettings() {
  const {isLoading, visibilitySettings} = useSelector(
    visibilitySettingsSelector
  );

  const setLoading = useActions(actions.setProductVisibilityLoading);
  const setVisibilitySettings = useActions(actions.setProductVisibility);

  useEffect(() => {
    if (visibilitySettings) return;
    setLoading(true);
    getProductVisibilityService()
      .then(res => {
        setVisibilitySettings(res);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setLoading, setVisibilitySettings, visibilitySettings]);

  return {
    isLoading,
    visibilitySettings,
  };
}

export function useProductVisibilitySettings() {
  const {isLoading, visibilitySettings} = useSelector(
    visibilitySettingsSelector
  );

  const setVisibilitySettings = useActions(actions.setProductVisibility);
  const setLoading = useActions(actions.setProductVisibilityLoading);

  const updateVisibilitySettings = (request: ProductVisibility) => {
    setLoading(true);
    setProductVisibilityService(request)
      .then(res => {
        setVisibilitySettings(res);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const toggleFeaturedProduct = () => {
    const enabledFeatured =
      visibilitySettings && !visibilitySettings.enabledFeatured;
    updateVisibilitySettings({
      enabledFeatured,
    });
  };

  const toggleRecentProduct = () => {
    const enabledRecent =
      visibilitySettings && !visibilitySettings.enabledRecent;
    updateVisibilitySettings({
      enabledRecent,
    });
  };

  return {
    updateVisibilitySettings,
    toggleFeaturedProduct,
    toggleRecentProduct,
    visibilitySettings,
    isLoading,
  };
}
